import {
  SkuSection,
  useGondolasSkuSectionsQuery,
  WithIndex,
} from "@/shared/api";
import { useMemo } from "react";
import { useGondolaChangeGroupsData } from "../hooks";
import { getCameraSkuSections } from "@/features/sku-sections/hooks/useCameraData";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import {
  selectCurrentCamera,
  selectSelectedPolygon,
  selectSelectedSkuSectionId,
  setSelectedPolygon,
  setSelectedProduct,
  setSelectedSkuSectionId,
} from "@/entities/annotation";
import {
  selectSelectedClosestGondolaDistItem,
  setSelectedDetectionId,
  setSelectedSkuSectionChangeId,
  setSelectedTriangulationId,
} from "@/entities/gondola-change-groups";
import { SKU_SECTION_COLORS } from "../lib/const";
import SkuSectionPolygons from "@/features/sku-sections/components/sku-section-polygons";
import { useTimelineData } from "@/features/annotation/hooks/useTimelineData";
import { Vector2d } from "konva/lib/types";

const planogramImageSize = {
  width: 1920,
  height: 1080,
};

const SkuSectionChangesPlanogram = () => {
  const dateRange = useAppSelector((state) => state.annotation.dateRange);
  const selectedOrder = useAppSelector(
    (state) => state.annotation.selectedOrder
  );
  const currentCamera = useAppSelector(selectCurrentCamera);
  const selectedClosestGondolaDistItem = useAppSelector(
    selectSelectedClosestGondolaDistItem
  );
  const canvasDimensions = useAppSelector(
    (state) => state.annotation.canvasDimensions
  );
  const selectedPolygon = useAppSelector(selectSelectedPolygon);
  const selectedSkuSectionId = useAppSelector(selectSelectedSkuSectionId);
  const dispatch = useAppDispatch();

  const {
    skuSectionChangesForCurrentCamera,
    skuSectionChangesForCurrentCameraGroupTableData,
    selectedGondolaChangeGroup,
    findFirstSkuSectionChangeWithDetection,
    handleSkuSectionProxsHighlighting,
    selectedSkuSectionChangeWithDetection,
  } = useGondolaChangeGroupsData();
  const {
    highlightIntersectedProxsByMatchScores,
    clearIntersectedProxsHighlighting,
  } = useTimelineData();

  const { data: skuSectionsData } = useGondolasSkuSectionsQuery(
    {
      gondolaId: String(selectedGondolaChangeGroup?.gondola_id),
      on_datetime: dateRange.start?.toISOString(),
    },
    { skip: !selectedGondolaChangeGroup?.gondola_id || !selectedOrder }
  );

  const skuSectionChangesMap = useMemo(() => {
    const map = new Map<
      number,
      { hasTriangulation: boolean; hasDetection: boolean }
    >();

    const skuSectionChanges =
      skuSectionChangesForCurrentCameraGroupTableData !== null
        ? skuSectionChangesForCurrentCameraGroupTableData
        : skuSectionChangesForCurrentCamera;

    skuSectionChanges.forEach((change) => {
      const existing = map.get(change.sku_section_id) || {
        hasTriangulation: false,
        hasDetection: false,
      };
      map.set(change.sku_section_id, {
        hasTriangulation: existing.hasTriangulation || !!change.triangulation,
        hasDetection: existing.hasDetection || !!change.detection,
      });
    });

    return map;
  }, [
    skuSectionChangesForCurrentCamera,
    skuSectionChangesForCurrentCameraGroupTableData,
  ]);

  const baseCameraSkuSections = skuSectionsData?.results
    ? getCameraSkuSections(skuSectionsData, currentCamera)
    : null;

  const cameraSkuSections = useMemo(() => {
    const getSkuSectionFillColor = (
      skuSectionId: number
    ): string | undefined => {
      if (selectedClosestGondolaDistItem && currentCamera) {
        const currentCameraSkuSections =
          selectedClosestGondolaDistItem
            .sku_section_ids_from_sku_sections_per_cam[currentCamera];
        if (
          currentCameraSkuSections &&
          currentCameraSkuSections.includes(skuSectionId)
        ) {
          return SKU_SECTION_COLORS.CLOSEST_GONDOLA_DIST;
        }
      }

      const changeInfo = skuSectionChangesMap.get(skuSectionId);

      if (!changeInfo) return SKU_SECTION_COLORS.NO_CHANGES;

      if (changeInfo.hasTriangulation) {
        return SKU_SECTION_COLORS.TRIANGULATION;
      }

      if (changeInfo.hasDetection) {
        return SKU_SECTION_COLORS.DETECTION;
      }

      return SKU_SECTION_COLORS.OCCLUSION;
    };

    return baseCameraSkuSections?.map((section) => ({
      ...section,
      color: getSkuSectionFillColor(section.sku_section_id),
    }));
  }, [
    baseCameraSkuSections,
    skuSectionChangesMap,
    selectedClosestGondolaDistItem,
    currentCamera,
  ]);

  const onSelectPolygon = (polygon: WithIndex<SkuSection>) => {
    const firstChangeWithDetection = findFirstSkuSectionChangeWithDetection(
      polygon?.sku_section_id
    );

    if (firstChangeWithDetection) {
      dispatch(
        setSelectedSkuSectionChangeId(
          firstChangeWithDetection?.gondola_change_camera_composite_id
        )
      );

      dispatch(
        setSelectedDetectionId(
          firstChangeWithDetection?.detection?.id?.toString() ||
            firstChangeWithDetection?.triangulation?.triangulation_id ||
            null
        )
      );

      dispatch(
        setSelectedTriangulationId(
          firstChangeWithDetection?.triangulation?.triangulation_id || null
        )
      );
    }

    if (selectedSkuSectionId === polygon.sku_section_id) {
      dispatch(setSelectedSkuSectionId(null));
      dispatch(setSelectedPolygon(null));
      dispatch(setSelectedProduct(null));
      handleSkuSectionProxsHighlighting(
        null,
        highlightIntersectedProxsByMatchScores,
        clearIntersectedProxsHighlighting
      );
      return;
    }

    dispatch(setSelectedSkuSectionId(polygon.sku_section_id));
    dispatch(setSelectedPolygon(polygon));
    dispatch(
      setSelectedProduct({
        name: polygon.variant.name,
        imageUrl: polygon.variant.image,
        barcode: polygon.variant.barcode,
        skuId: polygon.variant.id,
        variantId: polygon.variant.variant_id,
        count: 1,
        unitOfMeasurement: polygon.variant.uom,
        unitOfMeasurementInternal: polygon.variant.uom_internal,
      })
    );

    handleSkuSectionProxsHighlighting(
      polygon.sku_section_id,
      highlightIntersectedProxsByMatchScores,
      clearIntersectedProxsHighlighting
    );
  };

  const boundingBox = useMemo(() => {
    if (
      selectedSkuSectionChangeWithDetection?.detection &&
      selectedSkuSectionChangeWithDetection?.camera_local_id === currentCamera
    ) {
      return {
        topLeft: {
          x: selectedSkuSectionChangeWithDetection.detection.topleft[0],
          y: selectedSkuSectionChangeWithDetection.detection.topleft[1],
        } as Vector2d,
        bottomRight: {
          x: selectedSkuSectionChangeWithDetection.detection.bottomright[0],
          y: selectedSkuSectionChangeWithDetection.detection.bottomright[1],
        } as Vector2d,
      };
    }

    return null;
  }, [selectedSkuSectionChangeWithDetection, currentCamera]);

  return (
    <SkuSectionPolygons
      cameraSkuSections={cameraSkuSections || null}
      boundingBox={boundingBox}
      imageDimensions={canvasDimensions}
      selectedPolygonId={selectedPolygon?.sku_section_id}
      onPolygonClick={onSelectPolygon}
      originalImageSize={planogramImageSize}
    />
  );
};

export default SkuSectionChangesPlanogram;
